import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  StatusBar,
  Modal,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { BarCodeScanner } from 'expo-barcode-scanner';
import { Camera } from 'expo-camera';
import * as Linking from 'expo-linking';

/**
 * PreLoginScanScreen
 *
 * Allows guest users (not authenticated) to scan a QR code using the phone camera.
 * When a QR is scanned:
 *  - Store the scanned code in AsyncStorage as 'pending_qr_code'
 *  - Attempt to open the scanned value as a URL if it looks like one
 *  - Provide actions to Login or Register so redemption can happen after landing on Home
 */
export default function PreLoginScanScreen({ navigation }) {
  const [hasPermission, setHasPermission] = useState(null);
  const [scanned, setScanned] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    const requestPermission = async () => {
      try {
        const { status } = await Camera.requestPermissionsAsync();
        setHasPermission(status === 'granted');
      } catch (e) {
        console.log('Camera permission request failed:', e?.message || e);
        setHasPermission(false);
      }
    };
    requestPermission();
  }, []);

  const isProbablyUrl = useCallback((value) => {
    if (!value || typeof value !== 'string') return false;
    // Basic URL heuristic
    return /^(https?:\/\/|www\.)/i.test(value);
  }, []);

  const handleBarCodeScanned = async ({ type, data }) => {
    if (scanned) return;
    try {
      setScanned(true);
      setScannedData(data);
      // Save pending code for post-login redemption
      await AsyncStorage.setItem('pending_qr_code', data);
      setModalVisible(true);

      // Initiate a visit to the URL if the scanned data looks like a URL
      if (isProbablyUrl(data)) {
        try {
          await Linking.openURL(data);
        } catch (err) {
          console.log('Failed to open URL from QR:', err?.message || err);
        }
      }
    } catch (error) {
      console.log('Error handling scanned QR:', error?.message || error);
    }
  };

  const resetScanner = () => {
    setScanned(false);
    setScannedData('');
    setModalVisible(false);
  };

  const navigateToLogin = () => {
    setModalVisible(false);
    navigation.navigate('Login');
  };

  const navigateToRegister = () => {
    setModalVisible(false);
    navigation.navigate('Register');
  };

  // Manual input fallback (useful for web testing when camera isn't available)
  const [manualCode, setManualCode] = useState('');
  const handleManualSave = async () => {
    if (!manualCode) return;
    try {
      await AsyncStorage.setItem('pending_qr_code', manualCode);
      setScannedData(manualCode);
      setScanned(true);
      setModalVisible(true);
      if (isProbablyUrl(manualCode)) {
        try {
          await Linking.openURL(manualCode);
        } catch (err) {
          console.log('Failed to open URL from manual QR:', err?.message || err);
        }
      }
    } catch (error) {
      console.log('Error saving manual QR:', error?.message || error);
    }
  };

  const renderScanOverlay = () => (
    <View style={styles.cameraOverlay}>
      <View style={styles.topOverlay} />
      <View style={styles.middleRow}>
        <View style={styles.sideOverlay} />
        <View style={styles.scanArea}>
          <View style={styles.scanFrame}>
            <View style={[styles.corner, styles.topLeft]} />
            <View style={[styles.corner, styles.topRight]} />
            <View style={[styles.corner, styles.bottomLeft]} />
            <View style={[styles.corner, styles.bottomRight]} />
          </View>
        </View>
        <View style={styles.sideOverlay} />
      </View>
      <View style={styles.bottomOverlay}>
        <Text style={styles.instructionText}>
          Position the QR code within the frame to scan
        </Text>
      </View>
    </View>
  );

  if (hasPermission === null) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Requesting camera permission...</Text>
      </View>
    );
  }

  if (hasPermission === false) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={{ marginBottom: 12 }}>No access to camera</Text>
        <TouchableOpacity style={styles.retryButton} onPress={() => navigation.goBack()}>
          <Text style={styles.retryButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" />
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#F8FAFC" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Scan QR (Guest)</Text>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.cameraContainer}>
        {/* <BarCodeScanner
          onBarCodeScanned={handleBarCodeScanned}
          style={StyleSheet.absoluteFillObject}
        />
        <View style={styles.overlay}>
          <View style={styles.scanFrame} />
          <Text style={styles.overlayText}>Arahkan QR ke dalam kotak</Text>
        </View> */}
        <Camera
          style={styles.camera}
          type={Camera.Constants.Type.back}
          // flashMode={flashOn ? Camera.Constants.FlashMode.torch : Camera.Constants.FlashMode.off}
          onBarCodeScanned={scanned ? undefined : handleBarCodeScanned}
          barCodeScannerSettings={{
            barCodeTypes: [BarCodeScanner.Constants.BarCodeType.qr],
          }}
        >
          {renderScanOverlay()}
        </Camera>
      </View>

      <View style={styles.footer}>
        {scanned ? (
          <TouchableOpacity style={styles.primaryButton} onPress={resetScanner}>
            <Ionicons name="refresh" size={18} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>Scan Ulang</Text>
          </TouchableOpacity>
        ) : (
          <View style={styles.hintRow}>
            <Ionicons name="scan-outline" size={18} color="#64748B" />
            <Text style={styles.hintText}>Pindai kode untuk menyimpan dan lanjut login</Text>
          </View>
        )}
        {!scanned && (
          <View style={styles.manualInputRow}>
            <TextInput
              value={manualCode}
              onChangeText={setManualCode}
              placeholder="Masukkan kode QR atau URL"
              placeholderTextColor="#94A3B8"
              style={styles.textInput}
              autoCapitalize="none"
            />
            <TouchableOpacity style={styles.primaryButton} onPress={handleManualSave}>
              <Ionicons name="save-outline" size={18} color="#FFFFFF" />
              <Text style={styles.primaryButtonText}>Simpan</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Modal after scan */}
      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Ionicons name="qr-code" size={28} color="#22D3EE" />
              <Text style={styles.modalTitle}>QR Tersimpan</Text>
            </View>
            <Text style={styles.modalMessage} numberOfLines={2}>
              {isProbablyUrl(scannedData)
                ? 'Kami membuka tautan dari QR. Silakan Login/Registrasi di halaman tersebut, lalu kembali ke aplikasi.'
                : 'Kode QR berhasil ditangkap. Silakan Login/Registrasi untuk menukarkan hadiah.'}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={[styles.modalButton, styles.secondaryButton]} onPress={navigateToLogin}>
                <Text style={styles.secondaryButtonText}>Login</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalButton, styles.primaryButtonSolid]} onPress={navigateToRegister}>
                <Text style={styles.primaryButtonSolidText}>Registrasi</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0F172A',
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)'
  },
  headerTitle: {
    color: '#F8FAFC',
    fontSize: 16,
    fontWeight: '600',
  },
  cameraContainer: {
    flex: 1,
    backgroundColor: '#000',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scanFrame: {
    width: 240,
    height: 240,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#22D3EE',
    backgroundColor: 'transparent',
  },
  overlayText: {
    color: '#F8FAFC',
    marginTop: 12,
  },
  footer: {
    padding: 16,
    backgroundColor: '#0F172A',
  },
  manualInputRow: {
    marginTop: 12,
    gap: 8,
  },
  textInput: {
    backgroundColor: '#111827',
    borderColor: '#1F2937',
    borderWidth: 1,
    color: '#F8FAFC',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  hintRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  hintText: {
    color: '#64748B',
    fontSize: 12,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#22D3EE',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  retryButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: '#22D3EE',
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: 320,
    maxWidth: '90%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalMessage: {
    fontSize: 13,
    color: '#334155',
    marginBottom: 12,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  modalButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
  },
  secondaryButton: {
    backgroundColor: '#F1F5F9',
  },
  secondaryButtonText: {
    color: '#0F172A',
    fontWeight: '600',
  },
  primaryButtonSolid: {
    backgroundColor: '#22D3EE',
  },
  primaryButtonSolidText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  camera: {
    flex: 1
  },
  cameraOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  topOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  middleRow: {
    flexDirection: 'row',
    height: 250,
  },
  sideOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  scanArea: {
    width: 250,
    height: 250,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scanFrame: {
    width: 200,
    height: 200,
    position: 'relative',
  },
  corner: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderColor: '#999',
    borderWidth: 3,
  },
  topLeft: {
    top: 0,
    left: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  topRight: {
    top: 0,
    right: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
  },
  bottomLeft: {
    bottom: 0,
    left: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
  },
  bottomRight: {
    bottom: 0,
    right: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  bottomOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  instructionText: {
    color: '#FFFFFF',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 22,
  },
});